import React, { useState, useEffect, useRef } from "react";

export default function CiclosTable({
  ciclos = [],
  loading,
  listError,
  reload,
  estadoOptions,
  formatDate,
  estadoBadgeClass,
  activoBadgeClass,
  canManageCiclos,
  canUpdateCiclo,
  canDeleteCiclo,
  abrirModalEditar,
  abrirModalEliminar,
}) {
  const minRows = 10;
  const [openMenuId, setOpenMenuId] = useState(null);
  const [menuPos, setMenuPos] = useState({ top: 0, right: 0 });
  const wrapRef = useRef(null);

  useEffect(() => {
    const handleOutside = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpenMenuId(null);
      }
    };
    document.addEventListener("mousedown", handleOutside);
    return () => document.removeEventListener("mousedown", handleOutside);
  }, []);

  if (loading) {
    return (
      <div className="management-card ciclos-table-card text-center py-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Cargando...</span>
        </div>
      </div>
    );
  }

  if (listError) {
    return (
      <div className="management-card management-alert d-flex align-items-center justify-content-between mb-4">
        <div className="me-3">
          <p className="mb-1">{listError}</p>
          <small className="text-muted">No se pudo obtener los ciclos.</small>
        </div>
        {reload && (
          <div className="d-flex gap-2 flex-wrap">
            <button type="button" className="btn btn-sm btn-outline-secondary" onClick={reload}>
              <i className="bi bi-arrow-repeat me-1" />
              Reintentar
            </button>
          </div>
        )}
      </div>
    );
  }

  if (!ciclos.length) {
    return (
      <div className="management-card ciclos-empty text-center py-5">
        <i className="bi bi-inboxes ciclos-empty-icon d-block mb-3" />
        <h5 className="fw-semibold mb-1">Aun no hay ciclos registrados</h5>
        <p className="text-muted mb-3">
          Crea el primer ciclo para organizar las convocatorias.
        </p>
        {reload && (
          <button type="button" className="btn btn-outline-secondary btn-sm" onClick={reload}>
            <i className="bi bi-arrow-repeat me-2" />
            Actualizar
          </button>
        )}
      </div>
    );
  }

  const emptyRows = Math.max(0, minRows - ciclos.length);

  return (
    <div className="management-card ciclos-table-card">
      <div className="d-flex align-items-center justify-content-between mb-3 flex-wrap gap-3">
        <div>
          <p className="management-label mb-1">Listado</p>
          <h5 className="mb-0">Ciclos registrados</h5>
        </div>
        <button
          type="button"
          className="btn btn-outline-secondary btn-sm"
          onClick={reload}
          disabled={loading}
        >
          <i className="bi bi-arrow-repeat me-2" />
          Actualizar
        </button>
      </div>
      <div className="management-table-wrapper">
        <table className="table align-middle management-table ciclos-table mb-0">
          <thead>
            <tr>
              <th>#</th>
              <th>Codigo</th>
              <th>Ciclo</th>
              <th>Centro</th>
              <th>Año</th>
              <th>Fechas</th>
              <th>Estado</th>
              <th>Activo</th>
              <th className="text-end">Acciones</th>
            </tr>
          </thead>
          <tbody ref={wrapRef}>
            {ciclos.map((item, index) => {
              const isMenuOpen = openMenuId === item.id;
              return (
                <tr key={item.id}>
                  <td>{index + 1}</td>
                  <td className="fw-semibold">{item.codigo}</td>
                  <td>
                    <div className="fw-semibold">{item.nombre}</div>
                    {item.observaciones && (
                      <small className="text-muted d-block">{item.observaciones}</small>
                    )}
                  </td>
                  <td>{item.centroLabel || "-"}</td>
                  <td>{item.anio || "-"}</td>
                  <td>
                    <div className="small">
                      <div>
                        <i className="bi bi-calendar-event me-1" />
                        {formatDate(item.fechaInicio)}
                      </div>
                      <div className="text-muted">
                        <i className="bi bi-flag me-1" />
                        {formatDate(item.fechaFin)}
                      </div>
                    </div>
                  </td>
                  <td>
                    <span className={`badge rounded-pill px-3 py-2 ${estadoBadgeClass(item.estado)}`}>
                      {(estadoOptions.find((opt) => opt.value === item.estado)?.label) ||
                        (item.estado || "Planificado")}
                    </span>
                  </td>
                  <td>
                    <span className={`badge rounded-pill px-3 py-2 ${activoBadgeClass(item.activo)}`}>
                      {item.activo ? "Activo" : "Inactivo"}
                    </span>
                  </td>
                  <td className="text-end">
                    {canManageCiclos ? (
                      <div className="management-actions-wrap">
                        <button
                          type="button"
                          className={`management-actions-toggle${isMenuOpen ? " is-open" : ""}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            if (!isMenuOpen) {
                              const rect = e.currentTarget.getBoundingClientRect();
                              setMenuPos({ top: rect.bottom + 4, right: window.innerWidth - rect.right });
                            }
                            setOpenMenuId(isMenuOpen ? null : item.id);
                          }}
                          title="Acciones"
                          aria-expanded={isMenuOpen}
                        >
                          <i className="bi bi-three-dots-vertical" />
                        </button>
                        {isMenuOpen && (
                          <ul className="management-actions-menu" style={{ position: "fixed", top: menuPos.top, right: menuPos.right, left: "auto" }}>
                            {canUpdateCiclo && (
                              <li>
                                <button
                                  type="button"
                                  className="menu-item"
                                  onClick={(e) => { e.stopPropagation(); setOpenMenuId(null); abrirModalEditar(item); }}
                                >
                                  <i className="bi bi-pencil-square" />
                                  Editar
                                </button>
                              </li>
                            )}
                            {canDeleteCiclo && (
                              <li>
                                <button
                                  type="button"
                                  className="menu-item is-danger"
                                  onClick={(e) => { e.stopPropagation(); setOpenMenuId(null); abrirModalEliminar(item); }}
                                >
                                  <i className="bi bi-trash" />
                                  Eliminar
                                </button>
                              </li>
                            )}
                          </ul>
                        )}
                      </div>
                    ) : (
                      <span className="text-muted small d-block">Sin permisos</span>
                    )}
                  </td>
                </tr>
              );
            })}
            {emptyRows > 0 &&
              Array.from({ length: emptyRows }).map((_, idx) => (
                <tr key={`ciclo-empty-${idx}`} className="ciclos-table-empty-row" aria-hidden="true">
                  <td colSpan={9}>&nbsp;</td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
